import {
	json,
	type LinksFunction,
	type MetaFunction,
	type LoaderFunctionArgs,
} from '@remix-run/node'
import {
	Form,
	Link,
	Links,
	Meta,
	Outlet,
	Scripts,
	ScrollRestoration,
	useLoaderData,
	useSubmit,
} from '@remix-run/react'
import { useRef, type ReactNode } from 'react'
import { HoneypotProvider } from 'remix-utils/honeypot/react'
import appleTouchIconAssetUrl from './assets/favicons/apple-touch-icon.png'
import faviconAssetUrl from './assets/favicons/favicon.svg'
import { GeneralErrorBoundary } from './components/error-boundary.tsx'
import { ProgressBar } from './components/progress-bar.tsx'
import { Toaster, useToast } from './components/toaster.tsx'
import { Button } from './components/ui/button.tsx'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuPortal,
	DropdownMenuTrigger,
} from './components/ui/dropdown-menu.tsx'
import { Icon, href as iconsHref } from './components/ui/icon.tsx'
import {
	ThemeSwitch,
	useOptionalTheme,
	useTheme,
} from './routes/resources+/theme-switch.tsx'
import tailwindStyleSheetUrl from './styles/tailwind.css?url'
import { getUserId, logout } from './utils/auth.server.ts'
import { ClientHintCheck, getHints } from './utils/client-hints.tsx'
import { prisma } from './utils/db.server.ts'
import { getEnv } from './utils/env.server.ts'
import { honeypot } from './utils/honeypot.server.ts'
import { useNonce } from './utils/nonce-provider.ts'
import { combineHeaders } from './utils/response.ts'
import { getDomainUrl } from './utils/route.ts'
import { type Theme, getTheme } from './utils/theme.server.ts'
import { getToast } from './utils/toast.server.ts'
import { getUserImgSrc, useOptionalUser, useUser } from './utils/user.ts'

export const links: LinksFunction = () =>
	[
		{ rel: 'preload', href: iconsHref, as: 'image' },
		{ rel: 'icon', href: '/favicon.ico', sizes: '48x48' },
		{ rel: 'icon', type: 'image/svg+xml', href: faviconAssetUrl },
		{ rel: 'apple-touch-icon', href: appleTouchIconAssetUrl },
		{
			rel: 'manifest',
			href: '/site.webmanifest',
			crossOrigin: 'use-credentials',
		} as const,
		{ rel: 'stylesheet', href: tailwindStyleSheetUrl },
	].filter(Boolean)

export const meta: MetaFunction = ({ data }) => {
	return [
		{ title: data ? 'Denman Dines' : 'Denman Dines | Error' },
		{
			name: 'description',
			content: 'All my favorite recipes in a one stop shop.',
		},
	]
}

export async function loader({ request }: LoaderFunctionArgs) {
	const userId = await getUserId(request)
	const user = userId
		? await prisma.user.findUniqueOrThrow({
				select: {
					id: true,
					name: true,
					username: true,
					image: { select: { id: true } },
				},
				where: { id: userId },
			})
		: null

	if (userId && !user) {
		console.info("this shouldn't happen...")
		await logout({ request, redirectTo: '/' })
	}

	const { toast, headers: toastHeaders } = await getToast(request)
	const honeyProps = honeypot.getInputProps()

	return json(
		{
			user,
			requestInfo: {
				hints: getHints(request),
				origin: getDomainUrl(request),
				path: new URL(request.url).pathname,
				userPrefs: {
					theme: getTheme(request),
				},
			},
			ENV: getEnv(),
			toast,
			honeyProps,
		},
		{
			headers: combineHeaders(toastHeaders),
		},
	)
}

function Document({
	children,
	nonce,
	theme = 'light',
	env = {},
}: {
	children: ReactNode
	nonce: string
	theme?: Theme
	env?: Record<string, string>
}) {
	return (
		<html lang="en" className={`${theme} h-full overflow-x-hidden`}>
			<head>
				<ClientHintCheck nonce={nonce} />
				<Meta />
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width,initial-scale=1" />
				<Links />
			</head>
			<body className="bg-background text-foreground">
				{children}
				<script
					nonce={nonce}
					dangerouslySetInnerHTML={{
						__html: `window.ENV = ${JSON.stringify(env)}`,
					}}
				/>
				<ScrollRestoration nonce={nonce} />
				<Scripts nonce={nonce} />
			</body>
		</html>
	)
}

export function Layout({ children }: { children: ReactNode }) {
	const data = useLoaderData<typeof loader | null>()
	const nonce = useNonce()
	const theme = useOptionalTheme()
	return (
		<Document nonce={nonce} theme={theme} env={data?.ENV}>
			{children}
		</Document>
	)
}

function App() {
	const data = useLoaderData<typeof loader>()
	const user = useOptionalUser()
	const theme = useTheme()

	useToast(data.toast)

	return (
		<>
			<div className="flex h-screen flex-col justify-between">
				<header className="container fixed top-0 z-20 h-14 self-center bg-background py-2 shadow md:py-4">
					<nav className="flex h-full items-center gap-4">
						<Link to="/">
							<div className="font-bold">denman dines</div>
						</Link>

						<span className="flex-1" />

						<ThemeSwitch userPreference={data.requestInfo.userPrefs.theme} />
						<div className="flex items-center gap-10">
							{user ? (
								<UserDropdown />
							) : (
								<Button asChild variant="default" size="sm">
									<Link to="/login">Log In</Link>
								</Button>
							)}
						</div>
					</nav>
				</header>

				<main className="container mt-16 flex flex-1">
					<Outlet />
				</main>
			</div>
			<Toaster closeButton position="top-center" theme={theme} />
			<ProgressBar />
		</>
	)
}

export default function AppWithProviders() {
	const data = useLoaderData<typeof loader>()
	return (
		<HoneypotProvider {...data.honeyProps}>
			<App />
		</HoneypotProvider>
	)
}

function UserDropdown() {
	const user = useUser()
	const submit = useSubmit()
	const formRef = useRef<HTMLFormElement>(null)

	const userImageSrc = getUserImgSrc(user.image?.id)
	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button variant="ghost" size="sm" className="gap-2">
					{userImageSrc ? (
						<img
							className="h-8 w-8 rounded-full object-cover"
							alt={user.name ?? user.username}
							src={userImageSrc}
						/>
					) : (
						<Icon name="person" size="md">
							<span className="sr-only">User Profile</span>
						</Icon>
					)}
					<span className="hidden text-body-sm md:block">
						{user.name ?? user.username}
					</span>
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuPortal>
				<DropdownMenuContent sideOffset={8} align="start">
					<DropdownMenuItem
						asChild
						onSelect={(e) => {
							e.preventDefault()
							submit(formRef.current)
						}}
					>
						<Form action="/logout" method="POST" ref={formRef}>
							<Icon className="text-body-md" name="exit">
								<button type="submit">Logout</button>
							</Icon>
						</Form>
					</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenuPortal>
		</DropdownMenu>
	)
}

export const ErrorBoundary = GeneralErrorBoundary
